import { FC, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Footer } from "../../components/footer/Footer";
import { message, Button, Tag, Tooltip, Modal, Input } from "antd";
import {
    PlayCircleOutlined,
    DownloadOutlined,
    ShareAltOutlined,
    InfoCircleOutlined,
    ArrowLeftOutlined,
    FullscreenOutlined,
    MutedOutlined,
    SoundOutlined         // 正确的音量开启图标
} from "@ant-design/icons";
import "../../styles/reset.scss";
import "./VideoPreview.scss";

interface Props { }

export const VideoPreview: FC<Props> = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [showInfo, setShowInfo] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // 从URL参数获取视频信息
    const videoUrl = searchParams.get('url');
    const videoName = searchParams.get('name');
    const videoStatus = searchParams.get('status');
    const videoCreateTime = searchParams.get('createTime');
    const videoMessage = searchParams.get('message');
    const videoId = searchParams.get('id');

    const handleBack = () => {
        navigate("/home");
    };

    const handleFullscreen = () => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        if (videoElement) {
            if (!isFullscreen) {
                if (videoElement.requestFullscreen) {
                    videoElement.requestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                }
            }
        }
    };

    const handleMute = () => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        if (videoElement) {
            videoElement.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const handlePlayPause = () => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        if (videoElement) {
            if (isPlaying) {
                videoElement.pause();
            } else {
                videoElement.play();
            }
        }
    };

    const handleDownload = () => {
        if (videoUrl) {
            const link = document.createElement('a');
            link.href = videoUrl;
            link.download = `${videoName || 'video'}.mp4`;
            link.click();
            message.success('开始下载视频');
        } else {
            message.error('视频链接不可用');
        }
    };

    const handleShare = () => {
        Modal.info({
            title: '分享视频',
            content: (
                <div>
                    <p>视频链接：</p>
                    <Input.TextArea
                        value={videoUrl || ''}
                        rows={3}
                        readOnly
                    />
                    <Button
                        type="primary"
                        style={{ marginTop: 8 }}
                        onClick={() => {
                            navigator.clipboard.writeText(videoUrl || '');
                            message.success('链接已复制到剪贴板');
                        }}
                    >
                        复制链接
                    </Button>
                </div>
            ),
        });
    };

    const handleTimeUpdate = () => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        if (videoElement && !isDragging) {
            setCurrentTime(videoElement.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        if (videoElement) {
            setDuration(videoElement.duration);
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
    };

    const handlePause = () => {
        setIsPlaying(false);
    };

    // 新增：处理进度条点击
    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        const progressBar = e.currentTarget;
        const rect = progressBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, clickX / rect.width));
        const newTime = percentage * duration;
        
        if (videoElement) {
            videoElement.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    // 新增：处理进度条拖动开始
    const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        handleProgressClick(e);
    };

    // 新增：处理进度条拖动
    const handleProgressMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isDragging) {
            e.preventDefault();
            handleProgressClick(e);
        }
    };

    // 新增：处理进度条拖动结束
    const handleProgressMouseUp = () => {
        setIsDragging(false);
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case '2': return 'green';
            case '1': return 'orange';
            case '0': return 'blue';
            case '3': return 'red';
            case '4': return 'blue';
            default: return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case '2': return '已完成';
            case '1': return '处理中';
            case '0': return '等待中';
            case '3': return '处理失败';
            case '4': return '排队中';
            default: return '未知';
        }
    };

    useEffect(() => {
        const videoElement = document.getElementById('video-player') as HTMLVideoElement;
        if (videoElement) {
            videoElement.addEventListener('timeupdate', handleTimeUpdate);
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
            videoElement.addEventListener('play', handlePlay);
            videoElement.addEventListener('pause', handlePause);

            return () => {
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
                videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
                videoElement.removeEventListener('play', handlePlay);
                videoElement.removeEventListener('pause', handlePause);
            };
        }
    }, []);

    // 新增：全局鼠标事件监听
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                e.preventDefault();
                const progressBar = document.querySelector('.progress-bar') as HTMLDivElement;
                if (progressBar) {
                    const rect = progressBar.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                    const newTime = percentage * duration;
                    
                    const videoElement = document.getElementById('video-player') as HTMLVideoElement;
                    if (videoElement) {
                        videoElement.currentTime = newTime;
                        setCurrentTime(newTime);
                    }
                }
            }
        };

        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('mousemove', handleGlobalMouseMove);

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isDragging, duration]);

    if (!videoUrl) {
        return (
            <div className="video-preview-page">
                <div className="error-container">
                    <h2>视频链接无效</h2>
                    <p>无法加载视频，请返回任务列表重新选择。</p>
                    <Button type="primary" onClick={handleBack}>
                        返回任务列表
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="video-preview-page">
            {/* 头部 - 应用生成页的头部样式 */}
            <header className="preview-header">
                <div className="header-container">
                    <div className="header-left">
                        <button className="back-btn" onClick={handleBack}>
                            <ArrowLeftOutlined />
                            <span>返回</span>
                        </button>
                        <h1 className="header-title">视频预览</h1>
                    </div>
                    <div className="header-actions">
                        <Tooltip title="视频信息">
                            <Button
                                icon={<InfoCircleOutlined />}
                                onClick={() => setShowInfo(!showInfo)}
                            >
                                信息
                            </Button>
                        </Tooltip>
                        <Tooltip title="分享视频">
                            <Button
                                icon={<ShareAltOutlined />}
                                onClick={handleShare}
                            >
                                分享
                            </Button>
                        </Tooltip>
                        <Tooltip title="下载视频">
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                            >
                                下载
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </header>

            {/* 主内容区 */}
            <main className="preview-main">
                <div className="preview-container">
                    {/* 视频播放区域 */}
                    <div className="video-container">
                        <video
                            id="video-player"
                            src={videoUrl}
                            className="video-player"
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onPlay={handlePlay}
                            onPause={handlePause}
                        />

                        {/* 自定义控制栏 */}
                        <div className={`custom-controls ${showControls ? 'show' : 'hide'}`}>
                            {/* 可拖动的进度条 */}
                            <div 
                                className="progress-bar"
                                onClick={handleProgressClick}
                                onMouseDown={handleProgressMouseDown}
                                onMouseMove={handleProgressMouseMove}
                                onMouseUp={handleProgressMouseUp}
                            >
                                <div 
                                    className="progress-fill"
                                    style={{ width: `${(currentTime / duration) * 100}%` }}
                                ></div>
                            </div>

                            <div className="controls-main">
                                <div className="controls-left">
                                    <Button
                                        type="text"
                                        icon={isPlaying ? <PlayCircleOutlined /> : <PlayCircleOutlined />}
                                        onClick={handlePlayPause}
                                        className="play-btn"
                                    />
                                    <span className="time-display">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </span>
                                </div>

                                <div className="controls-right">
                                    <Button
                                        type="text"
                                        icon={isMuted ? <MutedOutlined /> : <SoundOutlined />}
                                        onClick={handleMute}
                                        className="mute-btn"
                                    />
                                    <Button
                                        type="text"
                                        icon={<FullscreenOutlined />}
                                        onClick={handleFullscreen}
                                        className="fullscreen-btn"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 视频信息面板 */}
                    {showInfo && (
                        <div className="video-info-panel">
                            <h3>视频信息</h3>
                            <div className="info-item">
                                <span className="info-label">视频名称：</span>
                                <span className="info-value">{videoName}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">任务状态：</span>
                                <Tag color={getStatusColor(videoStatus || '')}>
                                    {getStatusText(videoStatus || '')}
                                </Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">创建时间：</span>
                                <span className="info-value">{videoCreateTime}</span>
                            </div>
                            {videoMessage && (
                                <div className="info-item">
                                    <span className="info-label">处理信息：</span>
                                    <span className="info-value">{videoMessage}</span>
                                </div>
                            )}
                            <div className="info-item">
                                <span className="info-label">任务ID：</span>
                                <span className="info-value">{videoId}</span>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
};